import React from 'react'
import { bool, func, shape } from 'prop-types'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { useContainerWidth, Button } from '@fs/zion-ui'
import { useUser } from '@fs/zion-user'
import { usePreference } from '@fs/zion-preferences'
import Couples from './Couples'
import AncestorCard from './AncestorCard'
import { useMiniTree } from './hooks/hooks'

const coupleContainerCss = css`
  display: flex;
  justify-content: center;
  width: 100%;
`

const submitCss = css`
  width: 100%;
  display: flex;
  justify-content: center;
`

const treeContainerCss = css`
  display: flex;
  flex-wrap: wrap;
  height: fit-content;
  justify-content: space-around;
  align-content: flex-start;
  position: relative;
  + button {
    display: block;
    margin: 0 auto;
    width: 150px;
  }
  + a {
    display: block;
    margin: 0 auto;
    width: 150px;
  }
`

export default function AncestryTree({ setIsTreeShowing, overlay }) {
  const user = useUser()
  const state = useMiniTree()
  const showRoman = usePreference('tree.showNamesInRomanScript')
  const [t] = useTranslation()
  const atWidth = useContainerWidth()
  const isMissingFather = !state.userTree[2].name && !state.userTree[2].surname
  const isMissingMother = !state.userTree[3].name && !state.userTree[3].surname

  const userContainerCSS = css`
    display: flex;
    justify-content: center;
    width: 100%;
    margin: 32px 0;
    box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.1);
    ${atWidth({ default: 'width: calc(100vw / 5.25)', md: 'width: 120px' })};
    ${atWidth({ default: 'height: calc(100vw / 3)', md: 'height: 175px' })};
  `

  const grandparentSpacerCss = css`
    ${atWidth({ default: 'width: 20px', md: 'width: 80px' })};
  `

  return (
    <>
      <div css={treeContainerCss}>
        <div css={coupleContainerCss}>
          <Couples
            side="left"
            which="grandparents"
            leftAncestor={state.userTree[4]}
            rightAncestor={state.userTree[5]}
            disabled={isMissingFather && user.signedIn}
            setIsTreeShowing={setIsTreeShowing}
            showRoman={showRoman}
          />
          <div css={grandparentSpacerCss} />
          <Couples
            side="right"
            which="grandparents"
            leftAncestor={state.userTree[6]}
            rightAncestor={state.userTree[7]}
            disabled={isMissingMother && user.signedIn}
            setIsTreeShowing={setIsTreeShowing}
            showRoman={showRoman}
          />
        </div>
        <div css={coupleContainerCss}>
          <Couples
            which="parents"
            leftAncestor={state.userTree[2]}
            rightAncestor={state.userTree[3]}
            setIsTreeShowing={setIsTreeShowing}
            showRoman={showRoman}
          />
        </div>
        <div css={userContainerCSS}>
          <AncestorCard personData={state.userTree[1]} setIsTreeShowing={setIsTreeShowing} showRoman={showRoman} />
        </div>
      </div>
      <div css={submitCss}>
        <Button data-testid="ancestry-tree-submit-button" emphasis="high" onClick={overlay.handleClick}>
          {t('poh.takeaway.submit', 'Submit')}
        </Button>
      </div>
    </>
  )
}

AncestryTree.propTypes = {
  setIsTreeShowing: func,
  overlay: shape({
    isOpen: bool.isRequired,
    open: func.isRequired,
    close: func.isRequired,
  }).isRequired,
}
